import { Component, OnDestroy, OnInit } from '@angular/core';
import { filter, Subscription, switchMap, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { IAppUrls } from '../../../shared/models/app-urls';
import { IUser } from '../../../shared/models/user';
import { AuthService } from '../../../shared/services/auth.service';
import { ConfigurationService } from '../../../shared/services/configuration.service';
import { SamlService } from '../../../shared/services/saml.service';
import { UserUtility } from '../../../shared/utilities/user-utility';
import { NavigationEnd, Router } from '@angular/router';
import { materialSymbols } from '../../../shared/constants/material-symbols';
import { IAppFeaturesSettings } from '../../../shared/models/app-features-settings';

// requires SF Lightning Out library to be loaded
declare var $Lightning: any;

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  sfAppLauncherContainerId: string = 'sfAppLauncherContainer';
  appFeaturesSettings: IAppFeaturesSettings | null = null;

  currentUser: IUser | null = null;
  isInternalUser = false;

  appUrls: IAppUrls | null = null;
  committeeWorkspaceUrl: string | null = null;
  sfAccessToken: string | null = null;
  isSalesforceJsLoaded = false;

  canAccessNormOrgPage = false;
  canAccessStandardsLibraryPage = false;
  canAccessStandardsLibrarySimPage = false;
  canAccessBallotsPage = false;
  canAccessCommitteeWorkspacePage = false;
  canShowProfileLink = false;
  isOktaUser = false;

  subscriptions: Subscription = new Subscription();

  isDashboardActive: boolean = false;
  private dashboardActivePaths: string[] = [
    '/dashboard',
    '/reports',
    '/report',
    '/guides-and-videos'
  ];

  isSaLibraryActive: boolean = false;
  private saLibraryActivePaths: string[] = [
    '/sa-library',
  ]

  apps = materialSymbols.apps;
  searchIcon = materialSymbols.search;
  person = materialSymbols.person;
  openInNew = materialSymbols.openInNew;
  logout = materialSymbols.logout;
  accountCircle = materialSymbols.accountCircle;

  constructor(
    private _authService: AuthService,
    private _samlService: SamlService,
    private _configService: ConfigurationService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.setupCurrentUserSubscription();
    this.setupAppUrlsSubscription();
    this.setupRouterEventsSubscription();
    this.setupAppFeaturesSettingsSubscription();
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }


  setupCurrentUserSubscription() {
    const currentUserSubscription = this._authService.currentUserSubject
      .pipe(
        filter(user => !!user),
        take(1)
      )
      .subscribe(user => {
        if (!user) return;

        this.currentUser = user;
        this.isInternalUser = UserUtility.isInternalUser(user);
        this.isOktaUser = this._authService.isOktaUser;

        this.setUserPermissions(user);
        this.setCommitteeWorkspaceUrl();
      });

    this.subscriptions.add(currentUserSubscription);
  }

  setUserPermissions(user: IUser) {
    if (!user) return;

    this.currentUser = user as IUser;

    this.isInternalUser = UserUtility.isInternalUser(user);

    this.canAccessBallotsPage = UserUtility.canAccessBallotsPage(user);
    this.canAccessNormOrgPage = UserUtility.canAccessNomOrgPage(user);
    this.canAccessStandardsLibraryPage = UserUtility.canAccessStandardsLibraryPage(user);
    this.canAccessStandardsLibrarySimPage = UserUtility.canAccessStandardsLibrarySimPage(user);
    this.canAccessCommitteeWorkspacePage = UserUtility.canAccessCommitteeWorkspacePage(user);
  }


  setupAppUrlsSubscription() {
    const appUrlSubscription = this._authService.currentUserSubject
      .pipe(
        filter(user => !!user),
        take(1),
        switchMap(() => this._configService.getAppUrls())
      )
      .subscribe({
        next: (appUrls) => {
          if (appUrls) {
            this.loadSalesforceJsLibrary(environment.salesForceAppLauncherUrl);
            appUrls = this.setPCMSUrl(appUrls);
            this.appUrls = appUrls;
            this.setCommitteeWorkspaceUrl();
            this.updateProfileLinkVisibility();
          }
        },
        error: (error) => {
          // TODO: Log to SumoLogic;
        }
      });

    this.subscriptions.add(appUrlSubscription);
  }

  setupRouterEventsSubscription() {
    this.subscriptions.add(this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.checkIfDashboardActive();
        this.checkIfSaLibraryActive();
      }));
  }

  setupAppFeaturesSettingsSubscription() {
    this.subscriptions.add(this._configService.getAppFeaturesSettings()
      .subscribe({
        next: (response) => {
          if (response.success && response.content) {
            this.appFeaturesSettings = response.content;
            console.log('App Features Settings loaded in Header:', this.appFeaturesSettings.algoliaSearch);
          }
        },
        error: (error) => {
          // TODO: Log to SumoLogic;
        }
      }));

  }


  loadSalesforceJsLibrary(url: string) {
    const body = <HTMLDivElement>document.body;
    const script = document.createElement('script');
    script.innerHTML = '';
    script.src = url;
    script.async = true;
    script.defer = true;
    body.appendChild(script);

    this.isSalesforceJsLoaded = true;
  }

  setPCMSUrl(appUrls: IAppUrls) {
    appUrls.pcmsUrl = UserUtility.getUsersPcmsUrl(this.isInternalUser, appUrls.pcmsUrl, this.isOktaUser);
    return appUrls;
  }

  handleLogout() {
    this._authService.logout();
  }

  handleOpenAppLauncher(callback?: () => void) {
    if (!this.isSalesforceJsLoaded) {
      return;
    }

    this.openAppLauncher(this.sfAccessToken, callback);
    this.getSfAccessTokenThenOpenAppLauncher(callback);
  }

  handleOpenAppLauncherPublicComment() {
    this.handleOpenAppLauncher();
  }

  handleOpenAppLauncherCommitteeWorkspace() {
    this.handleOpenAppLauncher();
  }

  updateProfileLinkVisibility() {
    this.canShowProfileLink = !!this.appUrls && !this.isInternalUser;
  }

  private openAppLauncher(accessToken: string | null, callback?: () => void) {
    if (!this.currentUser) {
      return;
    }

    const sfAppLauncherContainerId = this.sfAppLauncherContainerId;
    const request = {
      sessionId: accessToken,
      partnerServerUrl: this.currentUser.partnerServerURL,
      userId: this.currentUser.userIdCustom,
      userType: this.currentUser.userType
    }

    const createCommunitiesAppLauncher = function () {
      $Lightning.createComponent("c:CommunitiesAppLauncherExternal", request, sfAppLauncherContainerId, callback);
    };

    $Lightning.use("c:testLightningOut", createCommunitiesAppLauncher, this.appUrls?.iamUrl,);
  }

  private getSfAccessTokenThenOpenAppLauncher(callback?: () => void) {
    this._samlService.getSfAccessToken().subscribe(response => {
      if (response.success) {
        this.sfAccessToken = response.content;
        this.openAppLauncher(this.sfAccessToken, callback);
      }
    });
  }

  private checkIfDashboardActive() {
    const currentUrl = this.router.url;
    this.isDashboardActive = this.dashboardActivePaths.some(path => currentUrl.startsWith(path));
  }

  private checkIfSaLibraryActive() {
    const currentUrl = this.router.url;
    this.isSaLibraryActive = this.saLibraryActivePaths.some(path => currentUrl.startsWith(path));
  }

  private setCommitteeWorkspaceUrl(): void {
    if (!this.appUrls) {
      this.committeeWorkspaceUrl = null;
      return;
    }
    if (this.isOktaUser) {
      // hiding Committee Workspace link for internal Okta users and showing it for external Okta users.
      this.canAccessCommitteeWorkspacePage = this.canAccessCommitteeWorkspacePage && !this.isInternalUser;
      this.committeeWorkspaceUrl = this.appUrls.committeeWorkspaceUrlExternal;
      return;
    }

    this.committeeWorkspaceUrl = this.isInternalUser
      ? this.appUrls.committeeWorkspaceUrlInternal
      : this.appUrls.committeeWorkspaceUrlExternal;
  }
}
